import React, { useMemo } from 'react'
import { Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer'
import { maskCnpj } from '~/prix/utils/cnpj'
import ItineraryReportHeaderPdf from './ItineraryReportHeaderPdf.component'
import ItineraryReportFooterPdf from './ItineraryReportFooterPdf.component'
import { CompanyFeature } from './ItineraryReportPdf.types'
import useConfig from '~/prix/react/hooks/config'
import { sizeColors } from '~/design'
import { getCharFromAlphabet } from '~/prix/utils/types/string'
import { applyMask } from '~/prix/utils/types/mask'

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 65,
    paddingHorizontal: 40,
  },

  companyHeader: {
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 5,
  },

  companyHeaderText: {
    backgroundColor: '#F5F5F5',
    padding: 10,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },

  companyTitle: {
    fontSize: 14,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginBottom: 3,
  },

  orderBadge: {
    fontSize: 11,
    color: '#666',
  },

  section: {
    marginBottom: 10,
  },

  sectionTitle: {
    fontSize: 13,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginBottom: 10,
  },

  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 10,
  },

  infoRowHorizontal: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 10,
    flex: 1,
  },

  infoLabel: {
    // minWidth: '20%',
    fontSize: 11,
    // marginRight: 10,
    color: '#333',
  },

  infoValue: {
    // width: '60%',
    fontSize: 12,
    fontWeight: 500,
    color: '#000',
  },

  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EFEFEF',
    marginVertical: 15,
  },

  // Map section
  mapSection: {
    marginTop: 10,
  },

  mapTitle: {
    fontSize: 14,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginBottom: 10,
  },

  mapContainer: {
    display: 'flex',
    height: 280,
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #E0E0E0',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  mapLegend: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#F9F9F9',
    borderRadius: 4,
  },

  legendText: {
    fontSize: 9,
    color: '#666',
    lineHeight: 1.4,
  },

  navigationHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },

  //QR Code
  qrCodeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  qrCode: {
    width: 50,
    height: 50,
  },

  qrCodeLabel: {
    fontSize: 6.5,
    color: '#666',
    textAlign: 'center',
    marginTop: -3,
  },

  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
})

interface ItineraryReportCompanyPdfProps {
  company: CompanyFeature
  totalCompanies: number
  generatedAt?: Date
}

export default function ItineraryReportCompanyPdf({
  company,
  totalCompanies,
  generatedAt,
}: ItineraryReportCompanyPdfProps) {
  const config = useConfig()
  const apiKey = config.services.googleConfig.apiKey

  const props = company.properties
  console.log(props)
  const position = Number(props.position)

  const mapUrl = useMemo(() => {
    const center = {
      lat: company.geometry.coordinates[1],
      lng: company.geometry.coordinates[0],
    }

    const markerColor = (
      sizeColors[props.size as keyof typeof sizeColors] || sizeColors['Others']
    ).replace('#', '0x')

    const markers = `markers=color:${markerColor}|label:${getCharFromAlphabet(position)}|${center.lat},${center.lng}`

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap'
    const params = [
      `center=${center.lat},${center.lng}`,
      'zoom=18',
      'size=640x400',
      'scale=2', // Aumentar a resolução do mapa
      'maptype=roadmap',
      markers,
      `key=${apiKey}`,
    ].join('&')

    return `${baseUrl}?${params}`
  }, [apiKey])

  const qrCodeGoogleMaps = useMemo(() => {
    const center = {
      lat: company.geometry.coordinates[1],
      lng: company.geometry.coordinates[0],
    }

    const googleMapsUrl = `https://www.google.com/maps?q=${center.lat},${center.lng}`
    const encodedUrl = encodeURIComponent(googleMapsUrl)

    return `https://quickchart.io/qr?text=${encodedUrl}`
  }, [apiKey])

  return (
    <Page size='A4' style={styles.page}>
      <ItineraryReportHeaderPdf />

      <View style={styles.companyHeader}>
        <View style={styles.companyHeaderText}>
          <Text style={styles.companyTitle}>
            {props.corporateName || props.tradeName || props.name || 'Empresa sem nome'}
          </Text>
          <Text style={styles.orderBadge}>
            {position + 1}º atendimento de {totalCompanies}
          </Text>
        </View>

        <View style={styles.qrCodeContainer}>
          <Image src={qrCodeGoogleMaps} style={styles.qrCode} />
          <Text style={styles.qrCodeLabel}>QRCode Maps</Text>
        </View>
      </View>

      {/* Basic Info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Informações da empresa</Text>

        {props.cnpj && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>CNPJ:</Text>
            <Text style={styles.infoValue}>{maskCnpj(props.cnpj)}</Text>
          </View>
        )}

        {props.tradeName && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Nome Fantasia:</Text>
            <Text style={styles.infoValue}>{props.tradeName}</Text>
          </View>
        )}

        {props.corporateName && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Razão Social:</Text>
            <Text style={styles.infoValue}>{props.corporateName}</Text>
          </View>
        )}

        {props.size && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Porte:</Text>
            <Text style={styles.infoValue}>{props.size}</Text>
          </View>
        )}

        {props.segmentDescription && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Segmento:</Text>
            <Text style={styles.infoValue}>{props.segmentDescription}</Text>
          </View>
        )}

        {props.mainCnaeDescription && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>CNAE Principal:</Text>
            <Text style={styles.infoValue}>{props.mainCnaeDescription}</Text>
          </View>
        )}

        {(props.ageGroup || props.openingDate || props.registrationStatus) && (
          <View style={styles.row}>
            {props.openingDate && (
              <View style={styles.infoRowHorizontal}>
                <Text style={styles.infoLabel}>Data de Abertura:</Text>
                <Text style={styles.infoValue}>{applyMask(props.openingDate, 'date')}</Text>
              </View>
            )}

            {props.ageGroup && (
              <View style={styles.infoRowHorizontal}>
                <Text style={styles.infoLabel}>Maturidade:</Text>
                <Text style={styles.infoValue}>{applyMask(props.ageGroup, 'ageGroup')}</Text>
              </View>
            )}

            {props.registrationStatus && (
              <View style={styles.infoRowHorizontal}>
                <Text style={styles.infoLabel}>Status:</Text>
                <Text style={styles.infoValue}>
                  {applyMask(props.registrationStatus, 'registrationStatus')}
                </Text>
              </View>
            )}
          </View>
        )}

        {props.address && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Endereço Completo:</Text>
            <Text style={styles.infoValue}>{props.address}</Text>
          </View>
        )}

        {props.intersectionLengthKilometers && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Distância do anterior:</Text>
            <Text style={styles.infoValue}>
              {Number(props.intersectionLengthKilometers).toFixed(2)} km
            </Text>
          </View>
        )}

        {props.duration && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Tempo estimado:</Text>
            <Text style={styles.infoValue}>{props.duration} minutos</Text>
          </View>
        )}
      </View>

      {/* Map Section */}
      <View>
        {/* <Text style={styles.mapTitle}>Visualização da empresa em mapa</Text> */}
        <View style={styles.mapContainer}>
          <Image src={mapUrl} style={styles.image} />
        </View>
      </View>
      <ItineraryReportFooterPdf generatedAt={generatedAt} />
    </Page>
  )
}
